import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import { formatNumber } from '../../utils/formatNumber';
import { getAdminClient, adminService } from '../../services/adminService';
import Toast from '../../components/ui/Toast';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const AdminBookingsManagement = () => {
  const navigate = useNavigate();
  const {
    user,
    userProfile,
    session,
    sessionReady,
    loading: authLoading,
    authState,
    AUTH_STATE,
  } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [toastNotification, setToastNotification] = useState(null);
  const [editingStatus, setEditingStatus] = useState({});
  const [saving, setSaving] = useState(false);

  const canMutate = true;

  useEffect(() => {
    if (!sessionReady || authLoading) return;

    if (!session?.access_token || authState === AUTH_STATE.UNAUTHENTICATED || !user?.id) {
      setBookings([]);
      setLoading(false);
      setError('');
      return;
    }

    if (userProfile?.role !== 'admin') {
      setBookings([]);
      setLoading(false);
      setError('غير مصرح لك بعرض هذه الصفحة');
      return;
    }

    loadBookings();
  }, [sessionReady, authLoading, session?.access_token, authState, user?.id, userProfile?.role]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError('');

      const { data: { session: activeSession } } = await supabase.auth.getSession();
      if (!activeSession?.access_token) {
        setError('انتهت جلسة تسجيل الدخول. يرجى تسجيل الدخول مرة أخرى.');
        setBookings([]);
        return;
      }

      const db = getAdminClient();
      const { data, error: fetchError } = await db
        .from('bookings')
        .select(`
          *,
          customer:customer_id(id, full_name, email, phone, avatar_url),
          photographer:photographer_id(id, full_name, email, phone, avatar_url)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      if (fetchError) throw fetchError;

      const formattedBookings = data?.map(booking => ({
        id: booking?.id,
        customerName: booking?.customer?.full_name || 'عميل',
        customerEmail: booking?.customer?.email || '',
        customerPhone: booking?.customer?.phone || '',
        photographerName: booking?.photographer?.full_name || 'مصور',
        photographerEmail: booking?.photographer?.email || '',
        photographerPhone: booking?.photographer?.phone || '',
        photographerImage: booking?.photographer?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1bbd80f16-1763294584961.png',
        serviceType: booking?.service_type,
        photographyType: booking?.photography_type,
        date: booking?.booking_date,
        time: booking?.booking_time,
        location: booking?.location,
        city: booking?.city,
        price: parseFloat(booking?.price || 0),
        status: booking?.status,
        duration: booking?.duration,
        equipment: booking?.equipment || [],
        notes: booking?.notes,
        createdAt: booking?.created_at,
        updatedAt: booking?.updated_at
      }));

      setBookings(formattedBookings || []);
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError(err?.message || 'فشل تحميل الحجوزات');
    } finally {
      setLoading(false);
    }
  };

  const filteredBookings = useMemo(() => {
    let filtered = [...bookings];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.customerName?.toLowerCase().includes(query) ||
        booking.photographerName?.toLowerCase().includes(query) ||
        booking.serviceType?.toLowerCase().includes(query) ||
        booking.photographyType?.toLowerCase().includes(query) ||
        booking.location?.toLowerCase().includes(query) ||
        booking.city?.toLowerCase().includes(query) ||
        booking.id?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(booking => booking.city === cityFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.date);
        switch (dateFilter) {
          case 'today':
            return bookingDate.toDateString() === now.toDateString();
          case 'thisWeek':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return bookingDate >= weekAgo;
          case 'thisMonth':
            return bookingDate.getMonth() === now.getMonth() && bookingDate.getFullYear() === now.getFullYear();
          case 'upcoming':
            return bookingDate > now;
          case 'past':
            return bookingDate < now;
          default:
            return true;
        }
      });
    }

    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB - dateA;
    });
  }, [bookings, searchQuery, statusFilter, dateFilter, cityFilter]);

  const cities = useMemo(() => {
    const uniqueCities = [...new Set(bookings.map(b => b.city).filter(Boolean))];
    return uniqueCities.sort();
  }, [bookings]);

  const handleStatusChange = async (bookingId, newStatus) => {
    if (!canMutate) {
      setToastNotification({
        title: 'غير متاح',
        message: 'تعديل الحالة يتطلب Service Role Key',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)'
      });
      return;
    }

    try {
      setSaving(true);
      await adminService.setBookingStatus(bookingId, newStatus);
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
      setEditingStatus(prev => ({ ...prev, [bookingId]: null }));
      setToastNotification({
        title: 'تم التحديث',
        message: 'تم تحديث حالة الحجز بنجاح',
        icon: 'CheckCircle',
        iconColor: 'var(--color-success)'
      });
    } catch (err) {
      setToastNotification({
        title: 'خطأ',
        message: err?.message || 'فشل تحديث حالة الحجز',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)'
      });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_photographer_confirmation: { label: 'بانتظار المصور', color: 'var(--color-warning)', bgColor: 'rgba(245, 158, 11, 0.1)' },
      awaiting_payment: { label: 'بانتظار الدفع', color: '#f97316', bgColor: 'rgba(249, 115, 22, 0.1)' },
      paid_confirmed: { label: 'مدفوع ومؤكد', color: 'var(--color-success)', bgColor: 'rgba(16, 185, 129, 0.1)' },
      completed: { label: 'مكتمل', color: 'var(--color-primary)', bgColor: 'rgba(37, 99, 235, 0.1)' },
      cancelled_by_customer: { label: 'ملغي من العميل', color: 'var(--color-error)', bgColor: 'rgba(239, 68, 68, 0.1)' },
      cancelled_by_admin: { label: 'ملغي من المشرف', color: '#dc2626', bgColor: 'rgba(220, 38, 38, 0.1)' },
      expired: { label: 'منتهي', color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.1)' },
    };

    const config = statusConfig[status] || { label: status, color: '#6b7280', bgColor: 'rgba(107, 114, 128, 0.1)' };

    return (
      <span
        className="px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap"
        style={{ color: config.color, backgroundColor: config.bgColor }}
      >
        {config.label}
      </span>
    );
  };

  const ACTIVE_STATUSES = ['pending_photographer_confirmation', 'awaiting_payment', 'paid_confirmed'];
  const CANCELLED_STATUSES = ['cancelled_by_customer', 'cancelled_by_admin', 'expired'];

  const stats = useMemo(() => {
    const total = bookings.length;
    const active = bookings.filter(b => ACTIVE_STATUSES.includes(b.status)).length;
    const completed = bookings.filter(b => b.status === 'completed').length;
    const cancelled = bookings.filter(b => CANCELLED_STATUSES.includes(b.status)).length;
    const totalRevenue = bookings
      .filter(b => b.status === 'completed')
      .reduce((sum, b) => sum + b.price, 0);

    return { total, active, completed, cancelled, totalRevenue };
  }, [bookings]);

  if (!sessionReady || authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">
            {!sessionReady || authLoading ? 'جاري التحقق من الجلسة...' : 'جاري تحميل الحجوزات...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <main className="pt-8 pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
                إدارة الحجوزات - المشرف
              </h1>
              <p className="text-slate-600 text-lg">
                عرض وإدارة جميع حجوزات المنصة
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 flex items-center gap-2"
            >
              <Icon name="ArrowRight" size={18} />
              <span>العودة للوحة التحكم</span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-600">إجمالي الحجوزات</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-amber-600">{stats.active}</div>
              <div className="text-sm text-slate-600">نشطة</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
              <div className="text-sm text-slate-600">مكتملة</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
              <div className="text-sm text-slate-600">ملغاة / منتهية</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="text-2xl font-bold text-green-600">{formatNumber(stats.totalRevenue)}</div>
              <div className="text-sm text-slate-600">إجمالي الإيرادات (ر.س)</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="relative">
                <Icon name="Search" size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ابحث عن حجز..."
                  className="w-full pr-10 pl-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="all">جميع الحالات</option>
                <option value="pending_photographer_confirmation">بانتظار المصور</option>
                <option value="awaiting_payment">بانتظار الدفع</option>
                <option value="paid_confirmed">مدفوع ومؤكد</option>
                <option value="completed">مكتمل</option>
                <option value="cancelled_by_customer">ملغي من العميل</option>
                <option value="cancelled_by_admin">ملغي من المشرف</option>
                <option value="expired">منتهي</option>
              </select>

              {/* City Filter */}
              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="all">جميع المدن</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>

              {/* Date Filter */}
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="all">جميع التواريخ</option>
                <option value="today">اليوم</option>
                <option value="thisWeek">هذا الأسبوع</option>
                <option value="thisMonth">هذا الشهر</option>
                <option value="upcoming">قادمة</option>
                <option value="past">سابقة</option>
              </select>
            </div>
          </div>

          {/* Bookings List */}
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
              <p className="text-red-800">{error}</p>
              <button
                onClick={loadBookings}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                إعادة المحاولة
              </button>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
              <Icon name="Inbox" size={48} className="text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">لا توجد حجوزات</h3>
              <p className="text-slate-600">
                {searchQuery || statusFilter !== 'all' || dateFilter !== 'all' || cityFilter !== 'all'
                  ? 'لا توجد نتائج تطابق معايير البحث'
                  : 'لا توجد حجوزات في النظام'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">الحجز</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">العميل</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">المصور</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">النوع</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">التاريخ</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">المدينة</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">السعر</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">الحالة</th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {filteredBookings.map((booking) => {
                      const currentStatus = editingStatus[booking.id] ?? booking.status;
                      return (
                        <tr key={booking.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-mono text-slate-600">
                              #{String(booking.id).slice(0, 8)}
                            </div>
                            <div className="text-xs text-slate-500">
                              {new Date(booking.createdAt).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-semibold text-slate-900">{booking.customerName}</div>
                            <div className="text-xs text-slate-500">{booking.customerEmail}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <img
                                src={booking.photographerImage}
                                alt={booking.photographerName}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{booking.photographerName}</div>
                                <div className="text-xs text-slate-500">{booking.photographerEmail}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">{getPhotographyTypeLabelAr(booking.photographyType)}</div>
                            <div className="text-xs text-slate-500">{booking.serviceType}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900">
                              {new Date(booking.date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </div>
                            <div className="text-xs text-slate-500">{booking.time}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                            {booking.city}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                            {formatNumber(booking.price)} ر.س
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(booking.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <select
                                value={currentStatus}
                                onChange={(e) => setEditingStatus(prev => ({ ...prev, [booking.id]: e.target.value }))}
                                disabled={!canMutate || saving}
                                className="px-2 py-1 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/40"
                              >
                                <option value="pending_photographer_confirmation">بانتظار المصور</option>
                                <option value="awaiting_payment">بانتظار الدفع</option>
                                <option value="paid_confirmed">مدفوع ومؤكد</option>
                                <option value="completed">مكتمل</option>
                                <option value="cancelled_by_admin">ملغي من المشرف</option>
                                <option value="expired">منتهي</option>
                              </select>
                              {currentStatus !== booking.status && canMutate && (
                                <button
                                  onClick={() => handleStatusChange(booking.id, currentStatus)}
                                  disabled={saving}
                                  className="px-3 py-1 text-xs bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                                >
                                  حفظ
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Results Count */}
          {filteredBookings.length > 0 && (
            <div className="mt-4 text-sm text-slate-600 text-center">
              عرض {filteredBookings.length} من {bookings.length} حجز
            </div>
          )}
        </div>
      </main>

      {/* Toast Notification */}
      {toastNotification && (
        <Toast
          notification={toastNotification}
          onClose={() => setToastNotification(null)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default AdminBookingsManagement;
